import { boundLocalDatabaseEpoch, isLocalDatabaseEpochCurrent } from '@/services/db/LocalDatabaseEpoch';
import type { Session } from '@supabase/supabase-js';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { globalState } from '@/state/globalState';
import {
  createSessionBoundSupabaseClient,
  supabase,
} from '@/services/supabase';

export interface PendingWorkOrigin {
  userId: string;
  empresaId: string;
}

export interface PendingSyncSessionSnapshot extends PendingWorkOrigin {
  /** Hash local de session_id; usa hash do JWT quando a claim não existir. */
  sessionFingerprint: string;
  localDatabaseEpoch?: number;
  /** Mantido apenas em memória para as requisições deste ciclo. Nunca persistir ou registrar. */
  accessToken: string;
}

export class PendingSyncSessionChangedError extends Error {
  constructor() {
    super('A sessão mudou durante o processamento de pendências. A fila foi preservada.');
    this.name = 'PendingSyncSessionChangedError';
  }
}

const sha512Hex = (value: string): string => Array.from(nacl.hash(naclUtil.decodeUTF8(value)))
  .map(byte => byte.toString(16).padStart(2, '0'))
  .join('');

const readJwtSessionId = (accessToken: string): string | null => {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(naclUtil.encodeUTF8(naclUtil.decodeBase64(padded)));
    return typeof claims?.session_id === 'string' && claims.session_id.trim()
      ? claims.session_id.trim()
      : null;
  } catch {
    return null;
  }
};

const fingerprintFor = (session: Session): string => {
  const sessionId = readJwtSessionId(session.access_token);
  return sessionId
    ? sha512Hex(`session:${session.user.id}:${sessionId}`)
    : sha512Hex(`jwt:${session.user.id}:${session.access_token}`);
};

/** Captura o autor da intenção offline sem rede; dados incompletos ficam sem autoria. */
export const capturePendingWorkOrigin = (): PendingWorkOrigin | null => {
  const auth = globalState.user.get();
  const userId = auth.user?.id?.trim();
  const sessionUserId = auth.session?.user?.id?.trim();
  const profileUserId = auth.profile?.id?.trim();
  const empresaId = auth.profile?.empresa_id?.trim();

  if (
    !auth.isAuthenticated ||
    !userId ||
    userId !== sessionUserId ||
    userId !== profileUserId ||
    !empresaId
  ) {
    return null;
  }

  return { userId, empresaId };
};

/** Captura uma sessão validada pelo Auth server e perfil da mesma identidade. */
export const capturePendingSyncSession = async (): Promise<PendingSyncSessionSnapshot | null> => {
  try {
    const localDatabaseEpoch = boundLocalDatabaseEpoch();
    const { data: firstData, error: firstError } = await supabase.auth.getSession();
    const first = firstData.session;
    if (firstError || !first?.access_token || !first.user?.id) return null;

    const origin = capturePendingWorkOrigin();
    if (!origin || origin.userId !== first.user.id) return null;

    // Valida no servidor o JWT exato que ficará preso ao cliente deste ciclo.
    const { data: verifiedData, error: verifyError } = await supabase.auth.getUser(first.access_token);
    if (verifyError || verifiedData.user?.id !== first.user.id) return null;

    const { data: currentData, error: currentError } = await supabase.auth.getSession();
    const current = currentData.session;
    const currentOrigin = capturePendingWorkOrigin();
    if (
      !await isLocalDatabaseEpochCurrent(localDatabaseEpoch) ||
      currentError ||
      !current?.access_token ||
      !current.user?.id ||
      current.user.id !== origin.userId ||
      !currentOrigin ||
      currentOrigin.userId !== origin.userId ||
      currentOrigin.empresaId !== origin.empresaId ||
      fingerprintFor(current) !== fingerprintFor(first)
    ) {
      return null;
    }

    return Object.freeze({
      ...origin,
      localDatabaseEpoch,
      sessionFingerprint: fingerprintFor(current),
      accessToken: current.access_token,
    });
  } catch {
    return null;
  }
};

/** Revalida sessão e vínculo de empresa antes da próxima chamada remota ou ACK. */
export const isPendingSyncSessionCurrent = async (
  snapshot: PendingSyncSessionSnapshot
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    const current = data.session;
    const origin = capturePendingWorkOrigin();
    return !error && await isLocalDatabaseEpochCurrent(snapshot.localDatabaseEpoch) && Boolean(
      current?.access_token &&
      current.user?.id === snapshot.userId &&
      origin?.userId === snapshot.userId &&
      origin.empresaId === snapshot.empresaId &&
      fingerprintFor(current) === snapshot.sessionFingerprint
    );
  } catch {
    return false;
  }
};

/** Confirmação síncrona para o instante do CAS local após a validação remota. */
export const isPendingSyncSessionSnapshotStateCurrent = (
  snapshot: PendingSyncSessionSnapshot
): boolean => {
  const current = globalState.user.session.get();
  const origin = capturePendingWorkOrigin();
  return Boolean(
    current?.access_token &&
    current.user?.id === snapshot.userId &&
    origin?.userId === snapshot.userId &&
    origin.empresaId === snapshot.empresaId &&
    fingerprintFor(current) === snapshot.sessionFingerprint
  );
};

export const assertPendingSyncSessionCurrent = async (
  snapshot: PendingSyncSessionSnapshot
): Promise<void> => {
  if (!await isPendingSyncSessionCurrent(snapshot)) {
    throw new PendingSyncSessionChangedError();
  }
};

/** Mantém consultas thenable presas à sessão antes e depois da resposta remota. */
export const guardPendingSyncRemoteQuery = (query: any, snapshot: PendingSyncSessionSnapshot): any => {
  if (!query || (typeof query !== 'object' && typeof query !== 'function')) return query;
  return new Proxy(query, {
    get(target, property) {
      const value = Reflect.get(target, property, target);
      if (property === 'then' && typeof value === 'function') {
        return (resolve: ((value: any) => any) | undefined, reject: ((reason: any) => any) | undefined) => {
          const request = (async () => {
            await assertPendingSyncSessionCurrent(snapshot);
            const response = await value.call(target);
            await assertPendingSyncSessionCurrent(snapshot);
            return response;
          })();

          // O await assimila thenables e ignora o retorno de `then`; por isso,
          // a conclusão sempre passa pelos callbacks fornecidos pelo chamador.
          return request.then(resolve, reject);
        };
      }
      if (typeof value === 'function') {
        return (...args: any[]) => guardPendingSyncRemoteQuery(value.apply(target, args), snapshot);
      }
      return value;
    },
  });
};

export const createPendingSyncSupabaseClient = (
  snapshot: PendingSyncSessionSnapshot
) => createSessionBoundSupabaseClient(snapshot.accessToken);
